/**
 * Invitation Accept Page Component
 * Handles the flow when a candidate clicks on an invitation link
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, useUser, SignIn, SignUp } from '@clerk/clerk-react';
import { CheckCircle, XCircle, Clock, Target, AlertCircle, Loader2, ArrowRight, User } from 'lucide-react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import axios from 'axios';
import { API_BASE, getAuthHeaders } from '../../utils/api';

interface InvitationDetails {
    valid: boolean;
    simulation_id: string;
    simulation_title: string;
    simulation_description: string;
    simulation_role: string;
    simulation_difficulty: string;
    simulation_duration: string;
    org_id: string;
    invited_by: string;
    expires_at: string;
    candidate_email: string;
}

export function InvitationAcceptPage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const { isSignedIn, isLoaded: authLoaded } = useAuth();
    const { user, isLoaded: userLoaded } = useUser();

    const [loading, setLoading] = useState(true);
    const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [accepting, setAccepting] = useState(false);
    const [showAuth, setShowAuth] = useState<'signin' | 'signup' | null>(null);

    useEffect(() => {
        if (token) {
            validateInvitation();
        }
    }, [token]);

    useEffect(() => {
        if (authLoaded && userLoaded && isSignedIn && invitation?.valid) {
            handleAcceptInvitation();
        }
    }, [authLoaded, userLoaded, isSignedIn, invitation]);

    const validateInvitation = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.get(`${API_BASE}/invitations/validate/${token}`);
            
            if (response.data.status === 'success') {
                setInvitation(response.data.data);
            } else {
                setError(response.data.message || 'Invalid invitation');
            }
        } catch (err: any) {
            console.error('Failed to validate invitation:', err);
            setError(err.response?.data?.message || 'This invitation is invalid or has expired');
        } finally {
            setLoading(false);
        }
    };

    const handleAcceptInvitation = async () => {
        if (!token || accepting) return;

        try {
            setAccepting(true);
            const authHeaders = await getAuthHeaders();

            const response = await axios.post(
                `${API_BASE}/invitations/accept/${token}`,
                {},
                { headers: authHeaders }
            );

            if (response.data.status === 'success') {
                const data = response.data.data;
                navigate(`/app?invitation_accepted=${data.simulation_id}`);
            } else {
                setError(response.data.message || 'Failed to accept invitation');
            }
        } catch (err: any) {
            console.error('Failed to accept invitation:', err);
            setError(err.response?.data?.message || 'Failed to accept invitation');
        } finally {
            setAccepting(false);
        }
    };

    const getDifficultyColor = (difficulty: string) => {
        switch (difficulty) {
            case 'Easy': return 'bg-green-100 text-green-700 border-green-200';
            case 'Medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
            case 'Hard': return 'bg-red-100 text-red-700 border-red-200';
            default: return 'bg-gray-100 text-gray-700 border-gray-200';
        }
    };

    const formatExpiryDate = (dateStr: string) => {
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
            });
        } catch {
            return dateStr;
        }
    };

    if (loading || !authLoaded) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
                    <p className="text-slate-600">Validating invitation...</p>
                </div>
            </div>
        );
    }

    if (error || !invitation?.valid) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
                >
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <XCircle className="w-8 h-8 text-red-600" />
                    </div>
                    <h1 className="text-2xl font-bold text-slate-900 mb-3">Invalid Invitation</h1>
                    <p className="text-slate-600 mb-6">
                        {error || 'This invitation link is invalid or has expired. Please contact the person who sent you this invitation.'}
                    </p>
                    <button
                        onClick={() => navigate('/')}
                        className="px-6 py-2.5 bg-slate-900 text-white font-medium rounded-lg hover:bg-slate-800 transition-colors"
                    >
                        Go to Home
                    </button>
                </motion.div>
            </div>
        );
    }

    if (showAuth) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-md w-full"
                >
                    <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-4">
                        <h2 className="text-lg font-semibold text-white">
                            {showAuth === 'signup' ? 'Create Your Account' : 'Sign In'}
                        </h2>
                        <p className="text-emerald-100 text-sm">
                            to access your simulation assessment
                        </p>
                    </div>
                    <div className="p-6">
                        {showAuth === 'signup' ? (
                            <SignUp
                                routing="virtual"
                                signInUrl="/app/invitation"
                                afterSignUpUrl={`/app/invitation/${token}`}
                                appearance={{
                                    elements: {
                                        rootBox: 'w-full',
                                        card: 'shadow-none p-0',
                                        formButtonPrimary: 'bg-emerald-500 hover:bg-emerald-600'
                                    }
                                }}
                            />
                        ) : (
                            <SignIn
                                routing="virtual"
                                signUpUrl="/app/invitation"
                                afterSignInUrl={`/app/invitation/${token}`}
                                appearance={{
                                    elements: {
                                        rootBox: 'w-full',
                                        card: 'shadow-none p-0',
                                        formButtonPrimary: 'bg-emerald-500 hover:bg-emerald-600'
                                    }
                                }}
                            />
                        )}
                        <div className="mt-4 text-center">
                            <button
                                onClick={() => setShowAuth(showAuth === 'signup' ? 'signin' : 'signup')}
                                className="text-sm text-emerald-600 hover:text-emerald-700"
                            >
                                {showAuth === 'signup' 
                                    ? 'Already have an account? Sign in' 
                                    : "Don't have an account? Sign up"}
                            </button>
                        </div>
                    </div>
                </motion.div>
            </div>
        );
    }

    if (accepting) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
                    <p className="text-slate-600">Accepting invitation...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-lg w-full"
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-8 py-6">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                            <Target className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-white">Simulation Invitation</h1>
                            <p className="text-emerald-100">You've been invited to take an assessment</p>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="p-8 space-y-6">
                    {/* Simulation Details */}
                    <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
                        <h2 className="text-xl font-bold text-slate-900 mb-2">{invitation.simulation_title}</h2>
                        <p className="text-emerald-600 font-medium text-sm mb-3">Role: {invitation.simulation_role}</p>
                        <p className="text-slate-600 text-sm mb-4">{invitation.simulation_description}</p>
                        
                        <div className="flex flex-wrap gap-3">
                            <span className={clsx('px-3 py-1 rounded-lg text-sm font-medium border', getDifficultyColor(invitation.simulation_difficulty))}>
                                {invitation.simulation_difficulty}
                            </span>
                            <span className="flex items-center gap-1.5 px-3 py-1 bg-white border border-slate-200 rounded-lg text-sm text-slate-600">
                                <Clock className="w-4 h-4" />
                                {invitation.simulation_duration}
                            </span>
                        </div>
                    </div>

                    {/* Inviter Info */}
                    {invitation.invited_by && (
                        <div className="flex items-center gap-3 text-sm text-slate-600">
                            <User className="w-4 h-4" />
                            <span>Invited by <strong className="text-slate-900">{invitation.invited_by}</strong></span>
                        </div>
                    )}

                    {/* Expiry Notice */}
                    <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 px-4 py-3 rounded-lg">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span>This invitation expires on {formatExpiryDate(invitation.expires_at)}</span>
                    </div>

                    {/* Action */}
                    {isSignedIn ? (
                        <button
                            onClick={handleAcceptInvitation}
                            disabled={accepting}
                            className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-4 px-6 rounded-xl transition-colors shadow-lg shadow-emerald-500/25"
                        >
                            {accepting ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Accepting...
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="w-5 h-5" />
                                    Accept Invitation
                                    <ArrowRight className="w-5 h-5" />
                                </>
                            )}
                        </button>
                    ) : (
                        <div className="space-y-3">
                            <button
                                onClick={() => setShowAuth('signup')}
                                className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-4 px-6 rounded-xl transition-colors shadow-lg shadow-emerald-500/25"
                            >
                                Create Account & Accept
                                <ArrowRight className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => setShowAuth('signin')}
                                className="w-full flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium py-3 px-6 rounded-xl transition-colors"
                            >
                                Already have an account? Sign in
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="bg-slate-50 px-8 py-4 border-t border-slate-200">
                    <p className="text-xs text-slate-500 text-center">
                        By accepting this invitation, you agree to participate in the simulation assessment.
                        Your results will be shared with the organization.
                    </p>
                </div>
            </motion.div>
        </div>
    );
}

export default InvitationAcceptPage;

